import { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { chatbotsApi } from "@/api/endpoints/chatbots";
import { sitesApi } from "@/api/endpoints/sites";
import { tenantsApi } from "@/api/endpoints/tenants";
import { Icon } from "@/components/Icon";
import { useSession } from "@/contexts/SessionContext";
import { canManageContent } from "@/lib/rbac";
import { ChatbotOverviewTab } from "./chatbot-tabs/OverviewTab";
import { ChatbotWidgetTab } from "./chatbot-tabs/WidgetTab";
import { ChatbotKnowledgeTab } from "./chatbot-tabs/KnowledgeTab";
import { ChatbotPromptsTab } from "./chatbot-tabs/PromptsTab";
import { ChatbotConversationsTab } from "./chatbot-tabs/ConversationsTab";
import { ChatbotLeadsTab } from "./chatbot-tabs/LeadsTab";

type TabKey =
  | "overview"
  | "widget"
  | "knowledge"
  | "prompts"
  | "conversations"
  | "leads";

const TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "widget", label: "Widget" },
  { key: "knowledge", label: "Knowledge" },
  { key: "prompts", label: "Prompts" },
  { key: "conversations", label: "Conversations" },
  { key: "leads", label: "Leads" },
];

export function ChatbotDetailPage(): JSX.Element {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id ?? "";
  const navigate = useNavigate();
  const { me } = useSession();
  const [tab, setTab] = useState<TabKey>("overview");

  const currentQuery = useQuery({
    queryKey: ["tenants", "current"],
    queryFn: () => tenantsApi.current(),
  });
  const role = currentQuery.data?.role ?? "viewer";
  const canEdit = canManageContent(role);

  const chatbotQuery = useQuery({
    queryKey: ["chatbots", id],
    queryFn: () => chatbotsApi.get(id),
    enabled: !!id,
    retry: false,
  });

  const sitesQuery = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.list(),
  });

  const site = useMemo(() => {
    const list = sitesQuery.data?.sites ?? [];
    if (!chatbotQuery.data) return null;
    return list.find((s) => s.id === chatbotQuery.data.chatbot.site_id) ?? null;
  }, [sitesQuery.data, chatbotQuery.data]);

  const tenantSlug = useMemo(() => {
    if (!me) return "";
    const m = me.memberships.find((mm) => mm.tenant_id === me.current_tenant_id);
    return m?.slug ?? "";
  }, [me]);

  if (chatbotQuery.isLoading) {
    return (
      <div className="page" data-testid="chatbot-detail-page">
        <div className="empty">Loading chatbot…</div>
      </div>
    );
  }

  if (chatbotQuery.isError || !chatbotQuery.data) {
    return (
      <div className="page" data-testid="chatbot-detail-page">
        <div className="page-head">
          <div>
            <div className="page-title">Chatbot not found</div>
            <div className="page-sub">
              This chatbot may have been deleted or belongs to another workspace.
            </div>
          </div>
          <div className="page-actions">
            <button className="btn btn-outline btn-sm" onClick={() => navigate({ to: "/chatbots" })}>
              Back to chatbots
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { chatbot, embed_snippet } = chatbotQuery.data;

  return (
    <div className="page" data-testid="chatbot-detail-page">
      <div className="page-head">
        <div>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>
            <Link to="/chatbots" style={{ color: "inherit" }}>Chatbots</Link>
            <span style={{ margin: "0 6px" }}>/</span>
            <span className="mono">{chatbot.slug}</span>
          </div>
          <div className="page-title">{chatbot.name}</div>
          <div className="page-sub">
            {site ? (
              <>
                Site: <span className="mono">{site.domain}</span>
              </>
            ) : (
              "Site unlinked"
            )}{" "}
            ·{" "}
            <span className={"chip " + (chatbot.status === "active" ? "chip-success" : "chip-warning")}>
              {chatbot.status}
            </span>
            {chatbot.is_default ? (
              <>
                {" "}
                · <span className="chip chip-accent">Default</span>
              </>
            ) : null}
          </div>
        </div>
        <div className="page-actions">
          <Link to="/chatbots" className="btn btn-ghost btn-sm">
            <Icon name="chevron-right" size={12} style={{ transform: "rotate(180deg)" }} />
            Back
          </Link>
        </div>
      </div>

      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={"tab" + (tab === t.key ? " active" : "")}
            onClick={() => setTab(t.key)}
            data-testid={`chatbot-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <ChatbotOverviewTab
          chatbot={chatbot}
          embedSnippet={embed_snippet}
          siteDomain={site?.domain ?? ""}
          tenantSlug={tenantSlug}
        />
      ) : null}
      {tab === "widget" ? (
        <ChatbotWidgetTab chatbotId={chatbot.id} canEdit={canEdit} />
      ) : null}
      {tab === "knowledge" ? (
        <ChatbotKnowledgeTab chatbotId={chatbot.id} canEdit={canEdit} />
      ) : null}
      {tab === "prompts" ? (
        <ChatbotPromptsTab chatbotId={chatbot.id} canEdit={canEdit} />
      ) : null}
      {tab === "conversations" ? (
        <ChatbotConversationsTab chatbotId={chatbot.id} role={role} />
      ) : null}
      {tab === "leads" ? (
        <ChatbotLeadsTab chatbotId={chatbot.id} canEdit={canEdit} />
      ) : null}
    </div>
  );
}
